<script setup lang="ts">
// GNB 메뉴 행. Desktop(1024+)은 Pill 전체 나열, Tablet·Mobile은 "☰ 전체 메뉴" → BottomSheet.
// 예정 항목은 링크 없이 비활성 + "· 예정" 라벨로만 표시한다.
interface GnbItem {
  key: string
  label: string
  active?: boolean
  upcoming?: boolean
}

defineProps<{ items: GnbItem[], extraItems?: GnbItem[], sheetTitle?: string }>()
const emit = defineEmits<{ select: [key: string] }>()

const sheetOpen = ref(false)

function pick(item: GnbItem) {
  if (item.upcoming)
    return
  sheetOpen.value = false
  emit('select', item.key)
}
</script>

<template>
  <nav class="app-gnb" aria-label="주요 메뉴">
    <div class="gnb-desktop">
      <template v-for="item in items" :key="item.key">
        <span v-if="item.upcoming" class="nav-link is-upcoming" aria-disabled="true">{{ item.label }} · 예정</span>
        <button v-else type="button" class="nav-link" :class="{ 'is-active': item.active }" @click="pick(item)">
          {{ item.label }}
        </button>
      </template>
    </div>

    <div class="gnb-compact">
      <button type="button" class="ds-pill" @click="sheetOpen = true">
        ☰ 전체 메뉴
      </button>
    </div>

    <BottomSheet v-model="sheetOpen" :title="sheetTitle || '전체 메뉴'">
      <ul class="sheet-list">
        <li v-for="item in items" :key="item.key">
          <span v-if="item.upcoming" class="is-upcoming sheet-item" aria-disabled="true">{{ item.label }} · 예정</span>
          <button v-else type="button" class="sheet-item" :class="{ 'is-active': item.active }" @click="pick(item)">
            {{ item.label }}
          </button>
        </li>
      </ul>
      <ul v-if="extraItems?.length" class="sheet-list sheet-extra">
        <li v-for="item in extraItems" :key="item.key">
          <button type="button" class="sheet-item" @click="pick(item)">
            {{ item.label }}
          </button>
        </li>
      </ul>
    </BottomSheet>
  </nav>
</template>

<style scoped>
.app-gnb {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.gnb-desktop {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 가운데 정렬은 유지하되 Pill(채워진 배경) 대신, 선택된 메뉴만 텍스트 아래 밑줄로 표시한다. */
.nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--ink-sub);
  font: 600 14px/1 var(--font-sans);
  white-space: nowrap;
  box-sizing: border-box;
  flex-shrink: 0;
  cursor: pointer;
}
.nav-link:hover {
  color: var(--ink);
}
.nav-link.is-active {
  color: var(--ink);
  font-weight: 700;
  border-bottom-color: var(--ink);
}
.nav-link.is-upcoming {
  color: var(--ink-dim);
  cursor: not-allowed;
}
.nav-link.is-upcoming:hover {
  color: var(--ink-dim);
}
.gnb-compact {
  display: none;
}
@media (max-width: 1023px) {
  .gnb-desktop {
    display: none;
  }
  .gnb-compact {
    display: block;
  }
}

.sheet-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.sheet-extra {
  margin-top: 12px;
}
.sheet-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0 4px;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: transparent;
  color: var(--ink-body);
  font: 600 var(--fs-body) / 1 var(--font-sans);
  white-space: nowrap;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}
.sheet-item.is-active {
  color: var(--ink);
  font-weight: 800;
}
.sheet-item.is-upcoming {
  color: #bbbbbb;
  cursor: not-allowed;
}
</style>
